import { type PropsWithChildren, useEffect, useRef } from 'react'

import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import { useTheme } from '@mui/material/styles'

import { APP_BAR_HEIGHT } from '../../nav'

export { SidebarLayoutPanel }

type Props = PropsWithChildren<{
  variant: 'permanent' | 'temporary'
  open: boolean
  onClose: () => void
  onExited?: () => void
  width: number
  anchor: 'left' | 'right'
  component?: 'div' | 'nav' | 'aside'
  'data-testid'?: string
}>

/**
 * A panel beside the page. As 'permanent' it sits next to the page below the
 * AppBar and collapses fully when closed. As 'temporary' it overlays the page,
 * never wider than the viewport.
 */
const SidebarLayoutPanel = ({
  variant,
  open,
  onClose,
  onExited,
  width,
  anchor,
  component = 'div',
  'data-testid': dataTestId,
  children,
}: Props) => {
  const theme = useTheme()
  const onExitedRef = useRef(onExited)
  const wasOpen = useRef(open)

  useEffect(() => {
    onExitedRef.current = onExited
  })

  // Next to the page the panel closes with a CSS transition, which ends after
  // its duration
  useEffect(() => {
    const closed = wasOpen.current && !open

    wasOpen.current = open

    if (variant !== 'permanent' || !closed) return

    const timeout = setTimeout(
      () => onExitedRef.current?.(),
      theme.transitions.duration.shorter,
    )

    return () => clearTimeout(timeout)
  }, [variant, open, theme.transitions.duration.shorter])

  if (variant === 'temporary') {
    return (
      <Drawer
        data-testid={dataTestId}
        anchor={anchor}
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width, maxWidth: '100vw' } },
          transition: { onExited },
        }}
      >
        {children}
      </Drawer>
    )
  }

  return (
    <Box
      component={component}
      data-testid={dataTestId}
      aria-hidden={!open}
      inert={!open}
      sx={(theme) => ({
        zIndex: 1100,
        position: 'sticky',
        top: APP_BAR_HEIGHT,
        alignSelf: 'flex-start',
        flexShrink: 0,
        height: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
        width: open ? width : 0,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        [anchor === 'left' ? 'borderRight' : 'borderLeft']: open
          ? '1px solid'
          : 'none',
        borderColor: 'divider',
        transition: theme.transitions.create('width', {
          duration: theme.transitions.duration.shorter,
        }),
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto',
          // Keeps the content from reflowing while the panel opens or closes
          width,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
